import TextField from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useState, useEffect } from "react";
import { InvalidURLError } from "_shared/_errors/errors";


const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginRight: 0,
    width: '100%'
  },
  marginBottom: theme.spacing(1.5),
  minWidth: theme.spacing(40),
  ":first-of-type": {
    marginRight: theme.spacing(1.5)
  }
}));

type URLInputProps = {
  className?: string;
  variant?: TextFieldVariants;
  required?: boolean;
  url?: string;
  onChange?: (URL: string, error?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const URLInput = forwardRef(
  function URLInput(props: URLInputProps, _ref: React.ForwardedRef<HTMLDivElement>) {
  const { className, variant, required, url, onChange, onKeyDown } = props;

  const [URLFieldText, setURLFieldText] = useState<string>(url ?? '');
  const [URLFieldError, setURLFieldError] = useState<string>('');

  useEffect(() => {
    setURLFieldText(url ?? '');
  }, [url]);

  function buildURL(inputText?: string): URL | undefined {
    try {
      if (!inputText && required) throw new InvalidURLError("URL address is required");
      else if (!inputText) return;

      const urlValid: boolean = inputText.startsWith("https://");
      if (!urlValid) throw new InvalidURLError("URL must start with https://");

      return new URL(inputText);
    } catch (error) {
      if (error instanceof InvalidURLError) {
        throw error;
      }
      if (error instanceof TypeError) {
        throw new InvalidURLError("Invalid URL address");
      }
    }
  }

  function handleURLChanged(urlText: string) {
    try {
      const url = buildURL(urlText);
      if (url) {
        setURLFieldError('');
        onChange?.(urlText);
      }
    } catch (error) {
      if (!(error instanceof InvalidURLError)) return;
      setURLFieldError(error.message);
      onChange?.(urlText, error.message);
    }
  }

  return (
    <InputField
      className={className}
      label="Website URL"
      required={required}
      onChange={(event) => {
        setURLFieldText(event.target.value);
        handleURLChanged(event.target.value);
      }}
      value={URLFieldText}
      error={!!URLFieldError}
      helperText={URLFieldError}
      variant={variant}
      onKeyDown={onKeyDown}
    />
  );
});

export default URLInput;
